/**
 * BalancesReport — shared table for ledger-balance reports.
 * @file src/web/features/admin/components/BalancesReport.tsx
 *
 * Powers Cash/Bank Book, Receivables and Payables — all return the same
 * { rows: [{id,name,group,debit,credit}], totals } shape. Rows link to the
 * ledger statement; scoped to the sidebar entity + period.
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { SEO } from '../../../shared/components';
import { AdminPageHeader } from './AdminPageHeader';
import { PageLoading } from './PageLoading';
import { adminFetchJson } from '../lib/admin-api';
import { useCompany } from '../lib/company-context';
import { usePeriod } from '../lib/period-context';
import { PeriodBar } from './PeriodBar';

interface Row { id: string; name: string; group: string; debit: number; credit: number }
interface Data { rows: Row[]; totals: { debit: number; credit: number } }

const inr = (n: number) => (n ? n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '');

export function BalancesReport({ title, endpoint, icon: Icon, emptyText }: { title: string; endpoint: string; icon: LucideIcon; emptyText: string }) {
  const navigate = useNavigate();
  const { entityId } = useCompany();
  const { to } = usePeriod();
  const [data, setData] = useState<Data | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancel = false; setData(null); setError(null);
    adminFetchJson<Data>(endpoint, { query: { entityId: entityId ?? undefined, to: to || undefined } })
      .then((d) => !cancel && setData(d))
      .catch((e) => !cancel && setError(e instanceof Error ? e.message : 'Failed to load'));
    return () => { cancel = true; };
  }, [endpoint, entityId, to]);

  return (
    <>
      <SEO title={title} />
      <AdminPageHeader title={title} breadcrumbs={[{ label: 'Reports', href: '/admin/reports' }, { label: title }]} />
      <Link to="/admin/reports" className="mb-4 inline-flex items-center gap-1 text-sm text-ink-500 no-underline hover:text-ink-700"><ChevronLeft className="h-4 w-4" /> All reports</Link>
      <PeriodBar />

      {error && <div className="rounded-2xl border border-danger-200 bg-danger-50 px-6 py-4 text-sm text-danger-700">Failed to load: {error}</div>}
      {!data && !error && <PageLoading label={`Loading ${title.toLowerCase()}`} />}

      {data && data.rows.length === 0 && (
        <div className="rounded-2xl border border-dashed border-ink-300 bg-ink-50/50 px-6 py-16 text-center">
          <span className="mx-auto grid size-12 place-items-center rounded-2xl bg-ink-100 text-ink-400"><Icon className="h-6 w-6" /></span>
          <h3 className="mt-5 text-lg font-semibold text-ink-900">Nothing to show</h3>
          <p className="mt-1.5 text-sm text-ink-600">{emptyText}</p>
        </div>
      )}

      {data && data.rows.length > 0 && (
        <div className="overflow-x-auto rounded-2xl border border-ink-200 bg-white">
          <table className="w-full min-w-[36rem] text-sm">
            <thead>
              <tr className="border-b border-ink-100 text-xs uppercase tracking-wide text-ink-400">
                <th className="px-5 py-3 text-left font-medium">Ledger</th>
                <th className="px-5 py-3 text-left font-medium">Group</th>
                <th className="px-5 py-3 text-right font-medium">Debit</th>
                <th className="px-5 py-3 text-right font-medium">Credit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-50">
              {data.rows.map((r) => (
                <tr key={r.id} onClick={() => navigate(`/admin/ledgers/${r.id}`)} className="cursor-pointer hover:bg-ink-50/60">
                  <td className="px-5 py-2.5 font-medium text-ink-900">{r.name}</td>
                  <td className="px-5 py-2.5 text-ink-500">{r.group}</td>
                  <td className="px-5 py-2.5 text-right tabular-nums text-ink-800">{inr(r.debit)}</td>
                  <td className="px-5 py-2.5 text-right tabular-nums text-ink-800">{inr(r.credit)}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-ink-200 bg-ink-50 font-semibold text-ink-900">
                <td className="px-5 py-3" colSpan={2}>Total</td>
                <td className="px-5 py-3 text-right tabular-nums">{inr(data.totals.debit)}</td>
                <td className="px-5 py-3 text-right tabular-nums">{inr(data.totals.credit)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
